import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { HomeIcon, HeartIcon, ActivityIcon, BandageIcon, ArrowRightIcon, WindIcon, UtensilsIcon, DropletIcon, ClipboardListIcon, ConciergeBellIcon, PillIcon, StethoscopeIcon, UserIcon } from 'lucide-react';
interface ServicesSectionProps {
  showViewAllButton?: boolean;
  limit?: number;
}
export function ServicesSection({
  showViewAllButton = true,
  limit
}: ServicesSectionProps) {
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  const services = [{
    title: 'Wound Care & Management',
    desc: 'Expert treatment for complex, chronic, and non-healing wounds utilizing advanced therapies.',
    icon: BandageIcon,
    color: 'bg-logo-blue',
    lightColor: 'bg-logo-blue/10',
    textColor: 'text-logo-blue',
    path: '/services/wound-care'
  }, {
    title: 'Tracheostomy (Trac) Care',
    desc: 'Specialized respiratory support for patients requiring long-term airway management.',
    icon: WindIcon,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    path: '/services/tracheostomy'
  }, {
    title: 'Enteral Nutrition (G-Tube Care)',
    desc: 'Expert Gastrostomy tube management, site care, and nutritional support.',
    icon: UtensilsIcon,
    color: 'bg-primary',
    lightColor: 'bg-primary/10',
    textColor: 'text-primary',
    path: '/services/g-tube'
  }, {
    title: 'Colostomy & Ostomy Care',
    desc: 'Comprehensive support, skin integrity management, and education for bowel diversions.',
    icon: HeartIcon,
    color: 'bg-accent',
    lightColor: 'bg-red-50',
    textColor: 'text-accent',
    path: '/services/colostomy-care'
  }, {
    title: 'Foley Catheter Management',
    desc: 'Professional urinary catheter care focused on comfort and infection prevention.',
    icon: DropletIcon,
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    path: '/services/foley-care'
  }, {
    title: 'Respite & Holiday Care',
    desc: 'Giving family caregivers a break while ensuring loved ones receive exceptional care.',
    icon: HeartIcon,
    color: 'bg-accent',
    lightColor: 'bg-red-50',
    textColor: 'text-accent',
    path: '/services/respite-care'
  }, {
    title: 'Planning & Funding Care',
    desc: 'Assistance with navigating care options and understanding funding resources.',
    icon: ClipboardListIcon,
    color: 'bg-primary',
    lightColor: 'bg-primary/10',
    textColor: 'text-primary',
    path: '/services/planning-funding'
  }, {
    title: 'Concierge Services',
    desc: 'Personalized assistance and coordination for various lifestyle and healthcare needs.',
    icon: ConciergeBellIcon,
    color: 'bg-logo-blue',
    lightColor: 'bg-logo-blue/10',
    textColor: 'text-logo-blue',
    path: '/services/concierge'
  }, {
    title: 'In-home Care Services',
    desc: 'Professional daily assistance and companionship to maintain independence at home.',
    icon: HomeIcon,
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    path: '/services/in-home-care'
  }, {
    title: 'Medication Reminders',
    desc: 'Ensuring safety and adherence through timely medication alerts and monitoring.',
    icon: PillIcon,
    color: 'bg-accent',
    lightColor: 'bg-red-50',
    textColor: 'text-accent',
    path: '/services/medication-reminders'
  }, {
    title: 'Occupational Therapy',
    desc: 'Helping patients regain daily living skills and adapt their home environment.',
    icon: StethoscopeIcon,
    color: 'bg-primary',
    lightColor: 'bg-primary/10',
    textColor: 'text-primary',
    path: '/services/occupational-therapy'
  }, {
    title: 'Personal Care Services',
    desc: 'Dignified assistance with hygiene, grooming, and other personal daily tasks.',
    icon: UserIcon,
    color: 'bg-logo-blue',
    lightColor: 'bg-logo-blue/10',
    textColor: 'text-logo-blue',
    path: '/services/personal-care'
  }, {
    title: 'Physical Therapy',
    desc: 'Restoring mobility and strength through personalized rehabilitation programs.',
    icon: ActivityIcon,
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    path: '/services/physical-therapy'
  }];
  const displayedServices = limit ? services.slice(0, limit) : services;
  return <section id="services" className="py-12 md:py-16 bg-light-alt relative overflow-hidden" data-id="element-332">
      {/* Decorative background elements */}
      <div className="absolute top-1/2 left-0 w-72 h-72 bg-accent/5 rounded-full -translate-x-1/2 blur-3xl" data-id="element-333"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full translate-x-1/4 -translate-y-1/4 blur-3xl" data-id="element-334"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-335">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-336">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-337">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-338">
              Our Services
            </h2>
            <div className="w-20 h-1.5 bg-accent mx-auto mb-6 rounded-full" data-id="element-339"></div>
            <p className="text-lg text-dark-muted" data-id="element-340">
              Comprehensive healthcare solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-341">
            {displayedServices.map((service, idx) => <div key={idx} className="bg-light rounded-2xl p-8 shadow-card hover:shadow-card-hover border border-gray-200 transition-all duration-300 transform hover:-translate-y-2 group flex flex-col h-full" data-id="element-342">
                <div className={`${service.lightColor} w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:${service.color} transition-colors duration-300`} data-id="element-343">
                  <service.icon className={`h-8 w-8 ${service.textColor} group-hover:text-white transition-colors duration-300`} data-id="element-344" />
                </div>
                <h3 className="text-xl font-bold text-dark mb-4" data-id="element-345">
                  {service.title}
                </h3>
                <p className="text-dark-muted mb-6 flex-grow" data-id="element-346">{service.desc}</p>
                <a href={`#${service.path}`} className={`inline-flex items-center ${service.textColor} font-semibold hover:opacity-80 transition-opacity mt-auto`} data-id="element-347">
                  Learn More <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-348" />
                </a>
              </div>)}
          </div>

          {showViewAllButton && <div className="text-center" data-id="element-349">
              <a href="#/services" className="inline-block bg-light-alt border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-300 shadow-sm" data-id="element-350">
                View All Services
              </a>
            </div>}
        </div>
      </div>
    </section>;
}